import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import { showSuccess } from '@/shared/lib/toast-helpers';
import type {
  AdvancePaymentRequest,
  AdvancePaymentResponse,
} from '@/shared/types/billing';

/**
 * Patient advance-payment add — POST `/api/Billing/Payment/Create/Patient/{patientId}`.
 *
 * Distinct from invoice-scoped `useAddPayment` (`use-payments.ts:137` →
 * `/Payment/Create/Invoice/{invoiceId}`). Different path-segment, different
 * body shape (no `isPaidFull` field — there's no outstanding to settle).
 *
 * Wire (Flutter `patient_billing_data_source_impl.dart:35-47` +
 * `advance_payment_request_model.dart`):
 *
 *   POST /api/Billing/Payment/Create/Patient/{patientId}
 *        body: AdvancePaymentRequest = {
 *          paymentMethod,   // "Cash" / "Card" / "Bank"  — magic strings verbatim
 *          paidAmount,      // number > 0
 *          note,
 *          receivedBy,      // current user id
 *          receiptDate,     // no-Z ISO (mirroring sibling AddPaymentRequest convention)
 *        }
 *        → AdvancePaymentResponse = { status, errorDetails, receiptId }
 *
 * Cache invalidations on success (mirror the post-receipt UX in Flutter
 * `patient_billing_bloc` — the bloc refetches both receipts list + balance
 * after a successful Add Advance Payment):
 *   - ['patient-advance-receipts', patientId]  (the receipts table refreshes)
 *   - ['patient-available-balance', patientId] (the balance card refreshes)
 */

export interface CreatePatientAdvancePaymentVars {
  patientId: string;
  request: AdvancePaymentRequest;
}

export function useCreatePatientAdvancePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, request }: CreatePatientAdvancePaymentVars) =>
      post<AdvancePaymentResponse>(
        `${BILLING_BASE}/Payment/Create/Patient/${patientId}`,
        request,
      ),
    onSuccess: (_data, vars) => {
      showSuccess('Add Payment successfully');
      void qc.invalidateQueries({
        queryKey: ['patient-advance-receipts', vars.patientId],
      });
      void qc.invalidateQueries({
        queryKey: ['patient-available-balance', vars.patientId],
      });
    },
  });
}
